import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  FaTimes,
  FaFileInvoice,
  FaCalendarAlt,
  FaReceipt,
  FaTasks,
  FaStickyNote,
  FaUsers,
  FaProjectDiagram,
  FaFileContract,
  FaConciergeBell,
  FaDatabase,
} from 'react-icons/fa';
import { apiHelper } from '../../utils/api';
import './RecordUsageModal.scss';

const MODULE_META = {
  quotes: { icon: <FaFileInvoice />, color: '#6366F1' },
  events: { icon: <FaCalendarAlt />, color: '#0EA5E9' },
  bills: { icon: <FaReceipt />, color: '#10B981' },
  checklists: { icon: <FaTasks />, color: '#F59E0B' },
  notes: { icon: <FaStickyNote />, color: '#EAB308' },
  team: { icon: <FaUsers />, color: '#8B5CF6' },
  projects: { icon: <FaProjectDiagram />, color: '#EC4899' },
  policies: { icon: <FaFileContract />, color: '#14B8A6' },
  services: { icon: <FaConciergeBell />, color: '#F97316' },
};

const formatActivity = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '-';
  }
};

const formatActivityTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString();
  } catch {
    return '';
  }
};

const formatFullDateTime = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return `${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}, ${d.toLocaleTimeString()}`;
  } catch {
    return '-';
  }
};

const RecordUsageModal = ({ user, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const userId = typeof user === 'string'
    ? user
    : (user?._id || user?.userId?._id || user?.userId);

  useEffect(() => {
    if (!userId) {
      setError('User not found');
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetchUsage = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiHelper.get(`/security/record-usage/${userId}`);
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) setError('Failed to load record usage');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchUsage();
    return () => { cancelled = true; };
  }, [userId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!user) return null;

  const displayUser = data?.user || (typeof user === 'object' ? user : {});
  const userName = displayUser?.name || (typeof user === 'object' ? (user?.name || user?.userId?.name) : '') || 'Unknown';
  const userEmail = displayUser?.email || (typeof user === 'object' ? (user?.email || user?.userId?.email) : '') || '';
  const modules = data?.modules || [];
  const totalRecords = data?.totalRecords ?? modules.reduce((s, m) => s + (m.count || 0), 0);
  const overallLastActivity = modules.reduce((latest, m) => {
    if (!m.lastActivity) return latest;
    if (!latest) return m;
    return new Date(m.lastActivity) > new Date(latest.lastActivity) ? m : latest;
  }, null);

  return ReactDOM.createPortal(
    <div className="record-usage-overlay" onClick={onClose}>
      <div className="record-usage-modal" onClick={(e) => e.stopPropagation()}>
        <div className="record-usage-modal__header">
          <div className="record-usage-modal__user">
            <div className="record-usage-modal__user-info">
              <h3 title={userName}>{userName}</h3>
              <p title={userEmail}>{userEmail}</p>
            </div>
            <div className="record-usage-modal__total">
              <FaDatabase />
              <div>
                <strong>{loading ? '…' : totalRecords}</strong>
                <span>Total</span>
              </div>
            </div>
          </div>
          <button className="record-usage-modal__close" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>
        <div className="record-usage-modal__body">
          {loading ? (
            <div className="record-usage-modal__loading">
              <div className="record-usage-modal__spinner" />
              <p>Loading record usage…</p>
            </div>
          ) : error ? (
            <div className="record-usage-modal__error">
              <p>{error}</p>
              <button
                className="record-usage-modal__retry"
                onClick={async () => {
                  setLoading(true);
                  setError(null);
                  try {
                    const res = await apiHelper.get(`/security/record-usage/${userId}`);
                    setData(res);
                  } catch {
                    setError('Failed to load record usage');
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="record-usage-grid">
              {modules.map((m) => {
                const meta = MODULE_META[m.key] || { icon: <FaFileInvoice />, color: '#6366F1' };
                return (
                  <div key={m.key} className="record-usage-card">
                    <div className="record-usage-card__icon" style={{ color: meta.color, background: `${meta.color}14` }}>
                      {meta.icon}
                    </div>
                    <div className="record-usage-card__content">
                      <span className="record-usage-card__label">{m.label}</span>
                      <strong className="record-usage-card__count">{m.count ?? 0}</strong>
                      {m.archived > 0 && (
                        <span className="record-usage-card__archived">+{m.archived} archived</span>
                      )}
                      <span className="record-usage-card__activity">
                        Last activity: {formatActivity(m.lastActivity)}
                        {m.lastActivity && (
                          <span className="record-usage-card__time"> {formatActivityTime(m.lastActivity)}</span>
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="record-usage-modal__footer">
          <span className="record-usage-modal__last">
            Last activity: <strong>{loading ? '…' : (overallLastActivity ? `${overallLastActivity.label} - ${formatFullDateTime(overallLastActivity.lastActivity)}` : '-')}</strong>
          </span>
          <button className="record-usage-modal__done" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RecordUsageModal;
